import { ChevronRight, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { EmptyState } from '../EmptyState';

/** One configuration surface a module's Settings page leads to. */
export interface SettingsMenuEntry {
  key: string;
  label: string;
  /** One line saying what this surface configures. It is the only prose on the row. */
  description: string;
  icon: LucideIcon;
  to: string;
  /**
   * Hides the row when the caller lacks it — never disables it.
   *
   * It is the key of the surface behind the row, which is usually a **different module's**
   * key from the one gating the Settings page itself: a settings menu collects the module's
   * configuration, and configuration lives in records of its own.
   */
  permission?: string;
}

/**
 * A module's Settings page: a **menu of the surfaces it configures**, and nothing else.
 *
 * **Settings is an extension of the sidebar, not a page with the lists inlined.** A module
 * grows configuration over time, and a page that renders each list in a section becomes a
 * scroll of unrelated tables where the reader cannot see what the module even has to
 * configure. One row per surface answers that at a glance, and the list itself is one click
 * away on its own page — where it gets the full width, its own breadcrumb, and its own
 * toolbar, exactly as a list anywhere else in the product does.
 *
 * The sidebar keeps one entry per module rather than one per setting, which is what this
 * page exists to make possible.
 */
export function SettingsMenu({
  entries,
  emptyMessage = 'There is nothing here you have permission to configure.',
}: {
  entries: readonly SettingsMenuEntry[];
  emptyMessage?: string;
}) {
  const { hasPermission } = useAuth();

  /*
    Filtered once, here, rather than by wrapping each row in a gate: the panel's dividers and
    its empty state both depend on how many rows survive, and a gate inside the loop would
    leave the panel drawing a border round nothing.
  */
  const visible = entries.filter((entry) => !entry.permission || hasPermission(entry.permission));

  if (visible.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white">
        <EmptyState message={emptyMessage} minHeight="min-h-[200px]" />
      </div>
    );
  }

  return (
    /*
      One continuous box with dividers between the rows — the same shape as the index list
      view, for the same reason: a stack of separated cards reads as unrelated things, and
      these are one menu.
    */
    <nav
      aria-label="Settings"
      className="divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200 bg-white"
    >
      {visible.map((entry) => {
        const Icon = entry.icon;

        return (
          <Link
            key={entry.key}
            id={`settings-menu-${entry.key}`}
            to={entry.to}
            className="flex items-center gap-3 px-4 py-3.5 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-200"
          >
            <span
              aria-hidden="true"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-slate-200 bg-slate-50 text-slate-500"
            >
              <Icon className="h-4 w-4" />
            </span>

            <span className="min-w-0 flex-1">
              <span className="block text-sm text-slate-800">{entry.label}</span>
              <span className="mt-0.5 block text-xs text-slate-500">{entry.description}</span>
            </span>

            <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" aria-hidden="true" />
          </Link>
        );
      })}
    </nav>
  );
}
